import React, { useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { fmtError } from "@/lib/api";
import { Repeat, User, Building2, Plus } from "lucide-react";
import EnableRoleModal from "@/components/EnableRoleModal";

/** Sélecteur de casquette dans la sidebar.
 *  - Si le user n'a qu'un rôle : affiche un bouton « Activer mon autre profil ».
 *  - Si le user a 2 rôles : affiche la bascule active/inactive et un bouton « Basculer ».
 *  Le compte admin n'est jamais concerné.
 */
export default function RoleSwitcher() {
  const { user, switchRole } = useAuth();
  const [busy, setBusy] = useState(false);
  const [modalTarget, setModalTarget] = useState(null);

  if (!user || user.role === "admin") return null;
  const roles = user.roles || [user.role];
  const other = user.role === "prestataire" ? "entreprise" : "prestataire";
  const hasBoth = roles.includes("prestataire") && roles.includes("entreprise");

  const doSwitch = async () => {
    setBusy(true);
    try {
      await switchRole(other);
      toast.success(other === "prestataire"
        ? "Vous êtes en mode prestataire."
        : "Vous êtes en mode donneur d'ordre.");
    } catch (err) {
      toast.error(fmtError(err));
    } finally {
      setBusy(false);
    }
  };

  const RoleIcon = user.role === "prestataire" ? User : Building2;
  const currentLabel = user.role === "prestataire" ? "Mode prestataire" : "Mode donneur d'ordre";
  const otherLabel = other === "prestataire" ? "prestataire" : "donneur d'ordre";

  return (
    <>
      <div className="p-3 border-b border-[#E4E7EB] bg-white text-xs" data-testid="role-switcher">
        <div className="flex items-center gap-2 text-[#3E4C59] mb-2">
          <RoleIcon size={14} className="text-[#1B3A5C]" />
          <span className="font-semibold text-[#1F2933]">{currentLabel}</span>
        </div>
        {hasBoth ? (
          <button
            onClick={doSwitch}
            disabled={busy}
            className="btn-outline w-full text-xs"
            data-testid="role-switch-btn"
            title={`Basculer en mode ${otherLabel}`}
          >
            <Repeat size={12} /> {busy ? "…" : `Basculer en ${otherLabel}`}
          </button>
        ) : (
          <button
            onClick={() => setModalTarget(other)}
            className="btn-outline w-full text-xs"
            data-testid="role-enable-btn"
          >
            <Plus size={12} /> Activer mon profil {otherLabel}
          </button>
        )}
      </div>
      {modalTarget && (
        <EnableRoleModal
          target={modalTarget}
          onClose={() => setModalTarget(null)}
          onEnabled={async (r) => {
            // Après activation, on bascule immédiatement dans le nouveau mode
            // — attendu du terrain : « je viens d'ajouter la casquette, je veux
            // l'utiliser tout de suite ».
            try { await switchRole(r); } catch { /* silencieux */ }
          }}
        />
      )}
    </>
  );
}
